/**
 * Custom Application Error Classes
 * Provides structured error types with context for better error handling
 */

/**
 * Base application error class
 * All custom errors should extend this class
 */
export class AppError extends Error {
  code: string;
  recoverable: boolean;
  userMessage: string;
  timestamp: string;

  constructor(message: string, code = 'APP_ERROR', recoverable = false, userMessage: string | null = null) {
    super(message);
    this.name = this.constructor.name;
    this.code = code;
    this.recoverable = recoverable;
    this.userMessage = userMessage || message;
    this.timestamp = new Date().toISOString();
    
    // Maintains proper stack trace for where error was thrown (V8 only)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor);
    }
  }

   toJSON(): Record<string, unknown> {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      recoverable: this.recoverable,
      userMessage: this.userMessage,
      timestamp: this.timestamp,
      stack: this.stack
    };
  }
}

/**
 * Network-related errors (fetch, HTTP, etc.)
 */
export class NetworkError extends AppError {
  url: string | null;
  statusCode: number | null;

  constructor(message: string, url: string | null = null, statusCode: number | null = null) {
    super(
      message,
      'NETWORK_ERROR',
      true, // Network errors are typically recoverable (retry)
      'Network error. Please check your connection and try again.'
    );
    this.url = url;
    this.statusCode = statusCode;
  }

  override toJSON(): Record<string, unknown> {
    return {
      ...super.toJSON(),
      url: this.url,
      statusCode: this.statusCode
    };
  }
}

/**
 * Data loading and parsing errors
 */
export class DataLoadError extends AppError {
  source: string | null;
  details: unknown;

  constructor(message: string, source: string | null = null, details: unknown = null) {
    super(
      message,
      'DATA_LOAD_ERROR',
      false, // Data errors usually require intervention
      'Failed to load data. Please refresh the page.'
    );
    this.source = source;
    this.details = details;
  }

  override toJSON(): Record<string, unknown> {
    return {
      ...super.toJSON(),
      source: this.source,
      details: this.details
    };
  }
}

/**
 * Authentication and authorization errors
 */
export class AuthError extends AppError {
  action: string | null;

  constructor(message: string, action: string | null = null) {
    super(
      message,
      'AUTH_ERROR',
      true, // Auth errors are recoverable (re-login)
      'Authentication failed. Please sign in again.'
    );
    this.action = action;
  }

  override toJSON(): Record<string, unknown> {
    return {
      ...super.toJSON(),
      action: this.action
    };
  }
}

/**
 * localStorage and storage quota errors
 */
export class StorageError extends AppError {
  key: string | null;
  operation: string | null;

  constructor(message: string, key: string | null = null, operation: string | null = null) {
    super(
      message,
      'STORAGE_ERROR',
      true, // Storage errors are often recoverable (clear data)
      'Storage error. Your device may be out of space.'
    );
    this.key = key;
    this.operation = operation;
  }

  override toJSON(): Record<string, unknown> {
    return {
      ...super.toJSON(),
      key: this.key,
      operation: this.operation
    };
  }
}

/**
 * Input validation errors
 */
export class ValidationError extends AppError {
  field: string | null;
  value: unknown;

  constructor(message: string, field: string | null = null, value: unknown = null) {
    super(
      message,
      'VALIDATION_ERROR',
      true, // Validation errors are recoverable (fix input)
      'Invalid input. Please check your data and try again.'
    );
    this.field = field;
    this.value = value;
  }

  override toJSON(): Record<string, unknown> {
    return {
      ...super.toJSON(),
      field: this.field,
      value: this.value
    };
  }
}

export default {
  AppError,
  NetworkError,
  DataLoadError,
  AuthError,
  StorageError,
  ValidationError
};
